import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { InviteState } from "@/types/invites"

/**
 * OKS-21 — Status pill for the invite link lifecycle (admin view).
 *
 * Distinct from `components/status-badge.tsx` (OKS-12 invitation
 * lifecycle: `pending|expired|accepted|revoked|active`) and
 * `components/candidates/candidate-status-badge.tsx` (OKS-17/18
 * candidate pipeline). Each enum is a different domain — keeping
 * them in distinct files avoids cross-domain coupling. Same shadcn
 * `Badge` primitive + same color-token system + same a11y pattern
 * (color + icon + text + `data-state`) keeps the visual language
 * consistent.
 *
 * **Server-authoritative.** FE never recomputes the state from
 * `expires_at` / nullable timestamps; the server returns the exact
 * discriminant and we render it. `data-state` exposes the prop value
 * verbatim so Vitest assertions key off the prop, not derived state.
 *
 * **Color is never the SOLE indicator** — every variant always
 * renders the localised text label and an `aria-label` carries the
 * full state name for screen readers.
 */

interface StatusVisual {
  /** Tailwind class string — uses standard color tokens, never raw hex. */
  className: string
}

const STATUS_VISUAL: Record<InviteState, StatusVisual> = {
  // Generated — neutral blue (link exists, awaiting first open)
  generated: {
    className:
      "border-blue-200 bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700/60",
  },
  // Clicked — amber (in motion, candidate is engaging)
  clicked: {
    className:
      "border-amber-200 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700/60",
  },
  // Signup started — amber-deep (closer to terminal)
  signup_started: {
    className:
      "border-amber-300 bg-amber-200 text-amber-950 dark:bg-amber-800/50 dark:text-amber-100 dark:border-amber-600/60",
  },
  // Completed — green (terminal happy)
  completed: {
    className:
      "border-emerald-300 bg-emerald-200 text-emerald-950 dark:bg-emerald-800/50 dark:text-emerald-100 dark:border-emerald-600/60",
  },
  // Expired — muted gray (terminal, soft)
  expired: {
    className:
      "border-zinc-300 bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:border-zinc-600/60",
  },
  // Revoked — muted gray (terminal, soft) — same visual as expired
  // (admin can still distinguish via the label text + `data-state`).
  revoked: {
    className:
      "border-zinc-300 bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:border-zinc-600/60",
  },
}

/**
 * Resolve the `invites.state.*` i18n label via literal `t()`
 * arguments so the inertia_i18n health scanner can statically prove
 * every key is reachable. Dynamic template strings
 * (`t(\`invites.state.${state}\`)`) silently bypass the scanner —
 * same precaution as `candidateStatusLabel` in OKS-17/18.
 */
function inviteStateLabel(
  t: ReturnType<typeof useTranslation>["t"],
  state: InviteState,
): string {
  switch (state) {
    case "generated":
      return t("invites.state.generated")
    case "clicked":
      return t("invites.state.clicked")
    case "signup_started":
      return t("invites.state.signup_started")
    case "completed":
      return t("invites.state.completed")
    case "expired":
      return t("invites.state.expired")
    case "revoked":
      return t("invites.state.revoked")
  }
}

interface InviteStatePillProps {
  state: InviteState
  className?: string
  /** Override the default testid. Defaults to `invites-panel-status-pill`
   *  per UX Spec §6. */
  testId?: string
}

export function InviteStatePill({
  state,
  className,
  testId = "invites-panel-status-pill",
}: InviteStatePillProps) {
  const { t } = useTranslation()
  const visual = STATUS_VISUAL[state]
  const label = inviteStateLabel(t, state)

  return (
    <Badge
      variant="outline"
      role="status"
      aria-label={label}
      data-testid={testId}
      data-state={state}
      className={cn(visual.className, className)}
    >
      {label}
    </Badge>
  )
}
